<template>
  <div class="wrapper">
    <div class="main k-login k-cr-acc">
      <div class="row">
        <div class="col-lg-6 col-md-6 col-sm-12">
          <div class="kl-inr-1">
            <router-link class="bck-log" to="/login">
              <i class="fa-solid fa-arrow-left-long"></i>
            </router-link>
            <div class="kl-top">
              <h3>Create an Account</h3>
            </div>
            <form class="login-page-form" @submit.prevent="createAccount">
              <div class="login-form-group login-email-group">
                <label for="create-email" class="login-label">Email:</label>
                <input
                  type="email"
                  id="create-email"
                  v-model="email"
                  class="login-input login-email-input"
                  placeholder="Email"
                  required
                />
                <p v-if="fieldErrors.email" class="field-error">
                  {{ fieldErrors.email }}
                </p>
              </div>

              <div class="login-form-group login-username-group">
                <label for="login-firstname" class="login-label">First Name:</label>
                <input
                  type="text"
                  id="login-firstname"
                  v-model="firstname"
                  class="login-input login-username-input"
                  placeholder="First Name"
                  required
                />
                <p v-if="fieldErrors.name" class="field-error">{{ fieldErrors.name }}</p>
              </div>

              <div class="login-form-group login-username-group">
                <label for="login-lastname" class="login-label">Last Name:</label>
                <input
                  type="text"
                  id="login-lastname"
                  v-model="lastname"
                  class="login-input login-username-input"
                  placeholder="Last Name"
                  required
                />
                <p v-if="fieldErrors.name" class="field-error">{{ fieldErrors.name }}</p>
              </div>

              <div class="login-form-group login-password-group">
                <label for="login-password" class="login-label">Password:</label>
                <div class="login-password-wrapper">
                  <input
                    :type="isPasswordVisible ? 'text' : 'password'"
                    id="login-password"
                    v-model="password"
                    class="login-input login-password-input"
                    placeholder="Password"
                    required
                  />
                  <button
                    type="button"
                    class="password-toggle-button"
                    @click="togglePasswordVisibility"
                  >
                    <i
                      :class="
                        isPasswordVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'
                      "
                    ></i>
                  </button>
                </div>
                <p v-if="fieldErrors.password" class="field-error">
                  {{ fieldErrors.password }}
                </p>
              </div>

              <div class="login-form-group login-confirm-password-group">
                <label for="confirm-password" class="login-label"
                  >Confirm Password:</label
                >
                <div class="login-password-wrapper">
                  <input
                    :type="isConfirmPasswordVisible ? 'text' : 'password'"
                    id="confirm-password"
                    v-model="confirmPassword"
                    class="login-input login-confirm-password-input"
                    placeholder="Confirm Password"
                    required
                  />
                  <button
                    type="button"
                    class="password-toggle-button"
                    @click="toggleConfirmPasswordVisibility"
                  >
                    <i
                      :class="
                        isConfirmPasswordVisible
                          ? 'fa-solid fa-eye-slash'
                          : 'fa-solid fa-eye'
                      "
                    ></i>
                  </button>
                </div>
                <p v-if="fieldErrors.confirmPassword" class="field-error">
                  {{ fieldErrors.confirmPassword }}
                </p>
              </div>

              <div class="login-form-group login-email-group">
                <label for="hospitalId" class="login-label">Hospital:</label>

                <select
                  v-model="hospitalId"
                  type="text"
                  id="hospitalId"
                  class="login-input"
                  required
                >
                  <option disabled value="">Select Hospital</option>
                  <option
                    v-for="hospital in hospitals"
                    :key="hospital.id"
                    :value="hospital.id"
                  >
                    {{ hospital.name }}
                  </option>
                </select>
                <p v-if="fieldErrors.required" class="field-error">
                  {{ fieldErrors.required }}
                </p>
              </div>

              <button
                type="submit"
                class="login-submit-button login-primary-button"
                :disabled="isLoading"
              >
                Create Account
                <span
                  v-if="isLoading"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </button>
            </form>
          </div>
        </div>
        <div class="col-lg-6 col-md-6 col-sm-12 col-r-kl">
          <div class="kl-inr-2">
            <img src="@/assets/img/mamabots-logo.png" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AuthService } from "@/services/auth.service";
import { HospitalsService } from "@/services/hospitals.service";
import { handleError, OBJECT_TYPES } from "@/services/util";
import { lowercaseString } from "@/utils/helpers";

export default {
  name: "KioskCreateAccount",
  data() {
    return {
      isPasswordVisible: false, // Track visibility state for password
      isConfirmPasswordVisible: false, // Track visibility state for confirm password
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      confirmPassword: "",
      hospitalId: "",
      fieldErrors: {}, // Field-specific error messages
      isLoading: false, // Loading state
      hospitals: [],
    };
  },
  async created() {
    try {
      const response = await HospitalsService.getList();
      if (response.success) {
        this.hospitals = response.data || [];
      } else {
        this.$store.commit("toast/NEW", {
          type: "error",
          message: response.message,
        });
      }
    } catch (error) {
      console.error("Error fetching hospitals:", error);
    }
  },
  methods: {
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible; // Toggle password visibility
    },
    toggleConfirmPasswordVisibility() {
      this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible; // Toggle confirm password visibility
    },
    async createAccount() {
      // Clear previous errors
      this.fieldErrors = {};

      // Validate password match
      if (this.password !== this.confirmPassword) {
        this.fieldErrors.confirmPassword = "Confirm Password does not match.";
        return;
      }

      this.isLoading = true;

      try {
        // Prepare user data for account creation
        const user = {
          firstname: this.firstname,
          lastname: this.lastname,
          email: lowercaseString(this.email),
          password: this.password,
          name: this.username,
          hospitalId: this.hospitalId,
          objectType: OBJECT_TYPES.KIOSK,
        };

        const response = await AuthService.makeCreateAccount(user);
        if (!response.success) {
          this.$store.commit("toast/NEW", { type: "error", message: response.message });
          return false;
        }
        this.$store.commit("toast/NEW", {
          type: "success",
          message: "Account created successfully!",
        });
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Delay for feedback
        this.$router.push("/login"); // Redirect to login page after successful account creation
      } catch (error) {
        console.log("🚀 ~ createAccount ~ error:", error);
        handleError(error, this.fieldErrors, this.$store); // Handle error messages
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>


<style scoped>
/* Hide browser's default password toggle */
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none;
}

input[type="password"]::-webkit-credentials-auto-fill-button,
input[type="password"]::-webkit-caps-lock-indicator,
input[type="password"]::-webkit-strong-password-visibility-indicator,
input[type="password"]::-webkit-textfield-decoration-container button,
input[type="password"]::-webkit-inner-spin-button,
input[type="password"]::-webkit-outer-spin-button {
  display: none;
  -webkit-appearance: none;
  appearance: none;
}

/* For Firefox */
input[type="password"] {
  -moz-appearance: textfield;
}

/* Ensure the password wrapper has proper positioning */
.login-password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.login-password-input {
  width: 100%;
  padding-right: 40px; /* Make space for your custom icon */
}

.password-toggle-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10;
  padding: 5px;
}

.password-toggle-button:focus {
  outline: none;
}

.password-toggle-button i {
  font-size: 1.2rem;
  color: #666;
}
</style>